/**
 * Ability sound families — pure, import-free vocabulary and resolver
 * (plan 29 F3, lane BU 2026-10-11).
 *
 * Before: one cast blip (`ability.cast`) for every ability, one melee hit
 * (`combat.melee.hit`) for every melee and area hit, and silent projectiles,
 * heals and buffs. Now every ability resolves to cues from a family: its
 * sound family (school, with lightning split out of nature) × the kind of
 * moment (cast / projectile launch / dash, impact by hit weight, heal land,
 * status apply). Absorb and immune have their own two cues.
 *
 * Shared by the compile gate (scripts/content/compile/playerAbilityContent.ts,
 * which may not pull game runtime modules), the TOML schema (an override must
 * name a family or a family cue), the runtime binder
 * (src/game/systems/audio/abilityAudio/) and the cue recipes
 * (src/game/systems/audio/cues/abilityFamily.cues.ts, one recipe per id here).
 * Authoring guide: docs/guides/H2KDB.md §Ability sounds.
 */

export const ABILITY_SOUND_FAMILIES = [
  'physical', 'holy', 'fire', 'frost', 'arcane', 'nature', 'shadow', 'lightning',
] as const;
export type AbilitySoundFamily = typeof ABILITY_SOUND_FAMILIES[number];

export const IMPACT_TIERS = ['light', 'medium', 'heavy'] as const;
export type ImpactTier = typeof IMPACT_TIERS[number];

/** The moments a family voices. `impact.*` carry the hit weight's tier. */
export const ABILITY_SOUND_KINDS = [
  'cast', 'launch', 'dash',
  'impact.light', 'impact.medium', 'impact.heavy',
  'heal', 'buff',
] as const;
export type AbilitySoundKind = typeof ABILITY_SOUND_KINDS[number];

/** Kinds that open an ability (played from the cast event). */
export type CastSoundKind = 'cast' | 'launch' | 'dash';

/** Defence cues — one each, whatever the school (BR's `absorbed` / `immune`). */
export const DEFENSE_CUES = {
  absorb: 'ability.defense.absorb',
  immune: 'ability.defense.immune',
} as const;

/** The cue every unknown ability falls back to (the pre-F3 cast blip). */
export const GENERIC_CAST_CUE = 'ability.cast';

export function abilityCueId(family: AbilitySoundFamily, kind: AbilitySoundKind): string {
  return `ability.${family}.${kind}`;
}

/** Every family cue id, family-major (physical.cast … lightning.buff). */
export const ABILITY_FAMILY_CUE_IDS: readonly string[] = ABILITY_SOUND_FAMILIES.flatMap(
  (family) => ABILITY_SOUND_KINDS.map((kind) => abilityCueId(family, kind)),
);

/** Every cue id this vocabulary can resolve to (families + defence). */
export const ABILITY_SOUND_CUE_IDS: readonly string[] = [
  ...ABILITY_FAMILY_CUE_IDS, DEFENSE_CUES.absorb, DEFENSE_CUES.immune,
];
const KNOWN_CUES = new Set(ABILITY_SOUND_CUE_IDS);
const FAMILIES = new Set<string>(ABILITY_SOUND_FAMILIES);

/** The fields the resolver reads (camelCase, as generated in abilities.gen.ts). */
export interface AbilitySoundRuleData {
  class?: string;
  school?: string;
  abilityType?: string;
  target?: string;
  baseDamage?: number;
  tags?: string[];
  vfx?: { hitWeight?: string; projectileShape?: string };
  statuses?: ReadonlyArray<{ kind: string }>;
  /** TOML `sound_cast`: a family name or a family cue id. */
  soundCast?: string;
  /** TOML `sound_impact`: a family name or a family cue id. */
  soundImpact?: string;
}

/** Content schools → families. `healing` voices holy; `chaos` voices shadow. */
const SCHOOL_FAMILY: Record<string, AbilitySoundFamily> = {
  physical: 'physical', holy: 'holy', healing: 'holy', fire: 'fire', frost: 'frost',
  arcane: 'arcane', nature: 'nature', shadow: 'shadow', chaos: 'shadow',
};

/** The ability's family: its school, with a `lightning`-tagged nature spell split out. */
export function soundFamilyForAbility(data: AbilitySoundRuleData | undefined): AbilitySoundFamily {
  const school = data?.school ?? 'physical';
  if (school === 'nature' && data?.tags?.includes('lightning')) return 'lightning';
  return SCHOOL_FAMILY[school] ?? 'physical';
}

/** Authored hit weight → impact tier (tap/light → light, heavy/epic → heavy). */
export function impactTierForWeight(hitWeight: string | undefined): ImpactTier {
  if (hitWeight === 'tap' || hitWeight === 'light') return 'light';
  if (hitWeight === 'heavy' || hitWeight === 'epic') return 'heavy';
  return 'medium';
}

/** Which opening the ability makes: a movement utility dashes, a projectile launches. */
export function castKindForAbility(data: AbilitySoundRuleData | undefined): CastSoundKind {
  if (!data) return 'cast';
  const tags = data.tags ?? [];
  if (data.abilityType === 'utility' && tags.includes('movement')) return 'dash';
  if (tags.includes('melee')) return 'cast';
  if (data.vfx?.projectileShape || tags.includes('projectile')) return 'launch';
  return 'cast';
}

const NON_DAMAGE_TYPES = new Set(['heal', 'defensive', 'buff']);

/** Same rule as playerAbilityRules.abilityDealsDamage (kept import-free here). */
function dealsDamage(data: AbilitySoundRuleData): boolean {
  if (data.abilityType === 'offensive') return true;
  if (data.abilityType === 'defensive' && data.target === 'enemy') return true;
  if (data.abilityType && NON_DAMAGE_TYPES.has(data.abilityType)) return false;
  return (data.baseDamage ?? 0) > 0 || (data.tags?.includes('melee') ?? false);
}

/** Does the ability put a status on someone (a buff, a shield, BR's statuses)? */
function appliesStatus(data: AbilitySoundRuleData): boolean {
  if ((data.statuses?.length ?? 0) > 0) return true;
  return data.abilityType === 'buff' || data.abilityType === 'defensive' || data.abilityType === 'debuff';
}

/** An override value is a family name or one of the family/defence cue ids. */
export function isValidSoundOverride(value: string): boolean {
  return FAMILIES.has(value) || KNOWN_CUES.has(value);
}

function applyOverride(
  override: string | undefined, fallbackFamily: AbilitySoundFamily, kind: AbilitySoundKind,
): string {
  if (!override) return abilityCueId(fallbackFamily, kind);
  if (FAMILIES.has(override)) return abilityCueId(override as AbilitySoundFamily, kind);
  return override;
}

export interface AbilitySounds {
  family: AbilitySoundFamily;
  castKind: CastSoundKind;
  /** Played from the cast event (local, or the remote cast broadcast). */
  cast: string;
  /** Played on each confirmed damage event; null for abilities that deal none. */
  impact: string | null;
  impactTier: ImpactTier | null;
  /** Played on each confirmed heal; null for non-heals. */
  heal: string | null;
  /** Played when the server applies the ability's status; null when it has none. */
  status: string | null;
}

/** Every cue an ability plays. Unknown abilities get the generic cast and nothing else. */
export function resolveAbilitySounds(data: AbilitySoundRuleData | undefined): AbilitySounds {
  const family = soundFamilyForAbility(data);
  const castKind = castKindForAbility(data);
  if (!data) {
    return { family, castKind, cast: GENERIC_CAST_CUE, impact: null, impactTier: null, heal: null, status: null };
  }
  const tier = dealsDamage(data) ? impactTierForWeight(data.vfx?.hitWeight) : null;
  return {
    family,
    castKind,
    cast: applyOverride(data.soundCast, family, castKind),
    impact: tier ? applyOverride(data.soundImpact, family, `impact.${tier}`) : null,
    impactTier: tier,
    heal: data.abilityType === 'heal' ? applyOverride(data.soundImpact, family, 'heal') : null,
    status: appliesStatus(data) ? abilityCueId(family, 'buff') : null,
  };
}

/** What each ability's sound setup gets wrong; empty = every cue it resolves exists. */
export function abilitySoundProblems(
  ids: Iterable<string>,
  abilityData: Readonly<Record<string, AbilitySoundRuleData | undefined>>,
): string[] {
  const problems: string[] = [];
  for (const id of [...ids].sort()) {
    const data = abilityData[id];
    if (!data) { problems.push(`${id}: not defined in content/abilities`); continue; }
    for (const [field, value] of [['sound_cast', data.soundCast], ['sound_impact', data.soundImpact]] as const) {
      if (value !== undefined && !isValidSoundOverride(value)) {
        problems.push(`${id}: ${field} = "${value}" is neither a family (${ABILITY_SOUND_FAMILIES.join(', ')}) nor an ability.<family>.<kind> cue`);
      }
    }
    const sounds = resolveAbilitySounds(data);
    for (const [moment, cue] of Object.entries({ cast: sounds.cast, impact: sounds.impact, heal: sounds.heal, status: sounds.status })) {
      if (cue !== null && !KNOWN_CUES.has(cue)) problems.push(`${id}: ${moment} resolves to unknown cue "${cue}"`);
    }
  }
  return problems;
}
